import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Menu,
  Avatar,
} from "@mui/material";
import {
  Delete,
  MoreVert,
  Visibility,
  LocationOn,
  CalendarToday,
  CheckCircle,
  Pending,
  PlayArrow,
  Error,
} from "@mui/icons-material";
import {
  Card,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import {
  DeliveriesModeSwitch,
  DeliveriesFilters,
  useDeliveriesFilters,
} from "@/components/deliveries";
import {
  deliveryService,
  Delivery,
  DeliveryStatus,
} from "@/services/delivery.service";
import { useToastStore } from "@/store/toastStore";
import { formatCustomerName } from "@/utils/formatCustomerName";

/** Formate une heure de livraison (ISO ou HH:mm) sans lever si invalide. */
function formatDeliveryTime(
  scheduledDeliveryTime: string | undefined | null,
  fallback: string,
): string {
  if (!scheduledDeliveryTime || !String(scheduledDeliveryTime).trim())
    return fallback;
  const s = String(scheduledDeliveryTime).trim();
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) || s.endsWith("Z")) {
    d = new Date(s);
  } else {
    d = new Date(`1970-01-01T${s}`);
  }
  if (!isValid(d)) return fallback;
  return format(d, "HH:mm");
}

/** Formate une date de livraison sans lever si invalide. */
function formatDeliveryDate(value: string | undefined | null): string {
  if (!value) return "N/A";
  const d = new Date(value);
  if (!isValid(d)) return "N/A";
  return format(d, "dd MMM yyyy", { locale: fr });
}

/**
 * Page de gestion des livraisons
 * Liste avec recherche, filtres et actions CRUD
 */
export function DeliveriesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const { filters } = useDeliveriesFilters();

  // État pour le menu d'actions de chaque ligne
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(
    null,
  );

  // État pour le dialog de confirmation
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: "delete" | "status";
    deliveryId: string | null;
    newStatus?: DeliveryStatus;
  }>({
    open: false,
    action: "delete",
    deliveryId: null,
  });

  const {
    data: deliveries = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: [
      "deliveries",
      "list",
      filters.status,
      filters.driver,
      filters.zone,
      filters.date,
    ],
    queryFn: () =>
      deliveryService.getAll({
        status: filters.status === "all" ? undefined : filters.status,
        driverId: filters.driver === "all" ? undefined : filters.driver,
        zoneId: filters.zone === "all" ? undefined : filters.zone,
        deliveryDate: filters.date || undefined,
      }),
  });

  // Mutation pour supprimer une livraison
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deliveryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      success("Livraison supprimée avec succès");
      setConfirmDialog({ open: false, action: "delete", deliveryId: null });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression de la livraison",
      );
    },
  });

  // Mutation pour mettre à jour le statut
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DeliveryStatus }) =>
      deliveryService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      success("Statut de la livraison mis à jour avec succès");
      setConfirmDialog({ open: false, action: "status", deliveryId: null });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la mise à jour du statut",
      );
    },
  });

  const filteredDeliveries = deliveries.filter((delivery) => {
    if (!filters.search) return true;
    const q = filters.search.toLowerCase();
    return (
      delivery.order?.orderNumber?.toLowerCase().includes(q) ||
      (delivery.order?.customer && formatCustomerName(delivery.order.customer).toLowerCase().includes(q)) ||
      delivery.order?.customer?.firstName?.toLowerCase().includes(q) ||
      delivery.order?.customer?.lastName?.toLowerCase().includes(q) ||
      delivery.driver?.firstName?.toLowerCase().includes(q) ||
      delivery.driver?.lastName?.toLowerCase().includes(q) ||
      delivery.zone?.name?.toLowerCase().includes(q)
    );
  });

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    delivery: Delivery,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedDelivery(delivery);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDelivery(null);
  };

  const handleAction = (
    action: "view" | "edit" | "updateStatus" | "delete",
    newStatus?: DeliveryStatus,
  ) => {
    if (!selectedDelivery) return;

    handleMenuClose();

    switch (action) {
      case "view":
        navigate(`/orders/${selectedDelivery.orderId}`);
        break;
      case "edit":
        // TODO: Créer une page d'édition de livraison
        navigate(`/deliveries/${selectedDelivery.id}/edit`);
        break;
      case "updateStatus":
        if (newStatus) {
          setConfirmDialog({
            open: true,
            action: "status",
            deliveryId: selectedDelivery.id,
            newStatus,
          });
        }
        break;
      case "delete":
        setConfirmDialog({
          open: true,
          action: "delete",
          deliveryId: selectedDelivery.id,
        });
        break;
    }
  };

  const handleConfirm = () => {
    if (confirmDialog.deliveryId) {
      if (confirmDialog.action === "delete") {
        deleteMutation.mutate(confirmDialog.deliveryId);
      } else if (confirmDialog.action === "status" && confirmDialog.newStatus) {
        updateStatusMutation.mutate({
          id: confirmDialog.deliveryId,
          status: confirmDialog.newStatus,
        });
      }
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialog({ open: false, action: "delete", deliveryId: null });
  };

  const getStatusIcon = (status: DeliveryStatus) => {
    switch (status) {
      case "DELIVERED":
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case "IN_PROGRESS":
        return <PlayArrow sx={{ fontSize: 16 }} />;
      case "FAILED":
        return <Error sx={{ fontSize: 16 }} />;
      case "PLANNED":
      default:
        return <Pending sx={{ fontSize: 16 }} />;
    }
  };

  const getNextStatusOptions = (
    currentStatus: DeliveryStatus,
  ): DeliveryStatus[] => {
    switch (currentStatus) {
      case "PLANNED":
        return ["IN_PROGRESS"];
      case "IN_PROGRESS":
        return ["DELIVERED", "FAILED"];
      case "DELIVERED":
      case "FAILED":
        return [];
      default:
        return [];
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement des livraisons..." />;
  }

  if (queryError) {
    return <ErrorMessage message="Erreur lors du chargement des livraisons." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Livraisons"
        action={<DeliveriesModeSwitch />}
      />

      <FiltersCard>
        <DeliveriesFilters />
      </FiltersCard>

      {/* Tableau des livraisons */}
      {filteredDeliveries.length === 0 ? (
        <EmptyState
          title="Aucune livraison trouvée"
          description={
            filters.search ||
            filters.status !== "all" ||
            filters.driver !== "all" ||
            filters.zone !== "all"
              ? "Aucune livraison ne correspond aux critères de recherche."
              : "Il n'y a aucune livraison pour cette date."
          }
        />
      ) : (
        <Card>
          <TableContainer
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 10 },
              "&::-webkit-scrollbar-track": { bgcolor: "action.hover" },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 5,
                bgcolor: "divider",
              },
            }}
          >
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Commande</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Livreur</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell>Date Livraison</TableCell>
                  <TableCell>Heure</TableCell>
                  <TableCell>Trajet</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDeliveries.map((delivery) => {
                  // Get next status options for this delivery status
                  getNextStatusOptions(delivery.status);

                  return (
                    <TableRow key={delivery.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontFamily: "monospace" }}
                        >
                          {delivery.order?.orderNumber || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {delivery.order?.customer
                            ? formatCustomerName(delivery.order.customer) || "N/A"
                            : "N/A"}
                        </Typography>
                        {(delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address) && (
                          <Typography variant="caption" color="text.secondary">
                            <LocationOn
                              sx={{
                                fontSize: 12,
                                verticalAlign: "middle",
                                mr: 0.5,
                              }}
                            />
                            {delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address ?? "—"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: "primary.main",
                              fontSize: 12,
                            }}
                          >
                            {delivery.driver?.firstName.charAt(0)}
                            {delivery.driver?.lastName.charAt(0)}
                          </Avatar>
                          <Typography variant="body2">
                            {delivery.driver
                              ? `${delivery.driver.firstName} ${delivery.driver.lastName}`
                              : "Non assigné"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={delivery.zone?.name || "N/A"}
                          size="small"
                          sx={{ bgcolor: "info.light", color: "info.dark" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CalendarToday
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {formatDeliveryDate(delivery.deliveryDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDeliveryTime(
                            delivery.scheduledDeliveryTime,
                            delivery.order?.deliveryTimeSlot || "-",
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {delivery.estimatedTravelTimeMinutes != null
                            ? `~${delivery.estimatedTravelTimeMinutes} min`
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(delivery.status)}
                          label={
                            delivery.status === "PLANNED"
                              ? "Planifiée"
                              : delivery.status === "IN_PROGRESS"
                                ? "En cours"
                                : delivery.status === "DELIVERED"
                                  ? "Livrée"
                                  : "Échouée"
                          }
                          size="small"
                          color={
                            delivery.status === "DELIVERED"
                              ? "success"
                              : delivery.status === "IN_PROGRESS"
                                ? "info"
                                : delivery.status === "FAILED"
                                  ? "error"
                                  : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, delivery)}
                          aria-label="Actions"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Menu d'actions */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: 3,
              },
            }}
          >
            <MenuItem onClick={() => handleAction("view")}>
              <Visibility sx={{ mr: 1 }} /> Voir la commande
            </MenuItem>
            {selectedDelivery &&
              getNextStatusOptions(selectedDelivery.status).length > 0 && (
                <>
                  {getNextStatusOptions(selectedDelivery.status).map(
                    (status) => (
                      <MenuItem
                        key={status}
                        onClick={() => handleAction("updateStatus", status)}
                      >
                        {status === "IN_PROGRESS" && (
                          <PlayArrow sx={{ mr: 1 }} />
                        )}
                        {status === "DELIVERED" && (
                          <CheckCircle sx={{ mr: 1 }} />
                        )}
                        {status === "FAILED" && <Error sx={{ mr: 1 }} />}
                        {status === "IN_PROGRESS"
                          ? 'Marquer "En cours"'
                          : status === "DELIVERED"
                            ? 'Marquer "Livrée"'
                            : 'Marquer "Échouée"'}
                      </MenuItem>
                    ),
                  )}
                </>
              )}
            <MenuItem
              onClick={() => handleAction("delete")}
              sx={{ color: "error.main" }}
            >
              <Delete sx={{ mr: 1 }} /> Supprimer
            </MenuItem>
          </Menu>

          {/* Compteur de résultats */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {filteredDeliveries.length} livraison
              {filteredDeliveries.length > 1 ? "s" : ""} trouvée
              {filteredDeliveries.length !== deliveries.length
                ? ` sur ${deliveries.length}`
                : ""}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog de confirmation */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.action === "delete"
            ? "Supprimer la livraison"
            : `Mettre à jour le statut`
        }
        message={
          confirmDialog.action === "delete"
            ? "Êtes-vous sûr de vouloir supprimer cette livraison ? Cette action est irréversible."
            : `Êtes-vous sûr de vouloir marquer cette livraison comme "${confirmDialog.newStatus === "IN_PROGRESS" ? "En cours" : confirmDialog.newStatus === "DELIVERED" ? "Livrée" : "Échouée"}" ?`
        }
        confirmText={
          confirmDialog.action === "delete" ? "Supprimer" : "Confirmer"
        }
        cancelText="Annuler"
        variant={confirmDialog.action === "delete" ? "danger" : "info"}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        loading={deleteMutation.isPending || updateStatusMutation.isPending}
      />
    </Box>
  );
}
